import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg, fmtNum } from "@/lib/api";
import { useTick } from "@/lib/ticks";
import { toast } from "sonner";
import { X } from "lucide-react";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

/**
 * Smartgain24-style Order popup with circuit-based validation.
 *
 * Type semantics (per user spec):
 *   MT  → straight Buy/Sell at market price (no Price field)
 *   RL  → Buy/Sell with Target attached. Price = target (auto-book on hit)
 *   SL  → Buy/Sell with Stop Loss attached. Price = SL trigger (auto-exit on hit)
 *
 * Circuit-Based rules (rule §7):
 *   BUY  + RL (Target):  Price > DayHigh AND Price ≤ UC
 *   BUY  + SL (StopLoss): Price < DayLow  AND Price ≥ LC
 *   SELL + RL (Target):  Price < DayLow  AND Price ≥ LC
 *   SELL + SL (StopLoss): Price > DayHigh AND Price ≤ UC
 *
 * Reject reasons: 1) Wrong direction  2) Circuit break  3) Invalid range
 */
export default function OrderModal({ open, onOpenChange, symbol, onBehalfClientId, side: initSide = "BUY", onPlaced }) {
  // Subscribe to ONLY this symbol — modal does not re-render on other symbols' ticks
  const tick = useTick(symbol);
  const [type, setType] = useState("MT");
  const [qty, setQty] = useState(1);
  const [price, setPrice] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setType("MT"); setQty(1); setPrice("");
  }, [symbol, open]);

  const meta = useMemo(() => {
    if (!tick) return null;
    const chgPts = tick.prev_close ? (tick.ltp - tick.prev_close) : 0;
    const chgPct = tick.prev_close ? ((tick.ltp - tick.prev_close) / tick.prev_close) * 100 : 0;
    const expiry = (tick.expiry || "").replace(/-/g, "").toUpperCase();
    return { chgPts, chgPct, expiry, lot: tick.lot_size, tick: tick.tick_size || 0.05 };
  }, [tick]);

  /** Validate Price field for RL/SL using zone-based rule.
   *  Allow zones: (DayHigh, UC]  OR  [LC, DayLow)
   *  Reject if: price ∈ [DayLow, DayHigh]  OR  price > UC  OR  price < LC
   */
  const validate = (side) => {
    if (!tick) return { ok: true };
    if (type === "MT") return { ok: true };

    const p = Number(price);
    if (price === "" || Number.isNaN(p) || p <= 0) {
      return { ok: false, reason: `Invalid range — ${type} order requires a valid price` };
    }

    // Tick-size alignment check (per user spec — every symbol uses its own exchange tick)
    const t = Number(tick.tick_size) || 0.05;
    const ratio = p / t;
    if (Math.abs(ratio - Math.round(ratio)) > 1e-4) {
      return { ok: false, reason: `Price must be in multiples of ${t} (tick size)` };
    }

    const { high: dh, low: dl, upper_circuit: uc, lower_circuit: lc } = tick;

    // Circuit break check (outermost band)
    if (p > uc) return { ok: false, reason: `Circuit break — Price must be ≤ UC ${fmtNum(uc)}` };
    if (p < lc) return { ok: false, reason: `Circuit break — Price must be ≥ LC ${fmtNum(lc)}` };

    // Wrong direction — price between DayLow & DayHigh is rejected
    if (p >= dl && p <= dh) {
      return {
        ok: false,
        reason: `Wrong direction — Price must be > Day High ${fmtNum(dh)} OR < Day Low ${fmtNum(dl)}`,
      };
    }

    return { ok: true };
  };

  // Live border feedback: green if price in upper-band OR lower-band; red otherwise
  const priceValidity = useMemo(() => {
    if (!tick || type === "MT" || price === "") return null;
    const p = Number(price);
    if (Number.isNaN(p) || p <= 0) return false;
    const { high: dh, low: dl, upper_circuit: uc, lower_circuit: lc } = tick;
    const upperOk = p > dh && p <= uc;
    const lowerOk = p < dl && p >= lc;
    return upperOk || lowerOk;
  }, [tick, type, price]);

  const submit = useCallback(async (side) => {
    if (!symbol) return;
    if (qty <= 0) return toast.error("Qty must be > 0");
    const v = validate(side);
    if (!v.ok) return toast.error("Order rejected", { description: v.reason });

    setBusy(true);
    try {
      const p = price === "" ? null : Number(price);
      const payload = {
        symbol, side,
        order_type: type,
        qty: Number(qty),
        price: type === "MT" ? null : p,
        target: null,
        stoploss: null,
      };
      if (onBehalfClientId) payload.on_behalf_client_id = onBehalfClientId;

      const r = await api.post("/orders", payload);
      const o = r.data;
      const tag = type === "RL" ? "with Target" : type === "SL" ? "with Stop Loss" : "";
      if (o.status === "EXECUTED") {
        toast.success(`Trade Executed @ ${fmtNum(o.executed_price)}`, {
          description: `${o.side} ${o.qty} lot ${o.symbol} ${tag} • Order ${o.order_no}`.trim(),
        });
      } else {
        toast.success(`Order Placed`, {
          description: `${o.side} ${o.qty} lot ${o.symbol} • ${o.order_type} ${tag} • ${o.order_no}`.trim(),
        });
      }
      onOpenChange(false);
      onPlaced && onPlaced(o);
    } catch (e) {
      toast.error("Order failed", { description: errMsg(e) });
    } finally {
      setBusy(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, qty, price, type, onBehalfClientId, onOpenChange, onPlaced, tick]);

  // Helper text under Price field — single rule for both RL and SL
  const ruleHint = useMemo(() => {
    if (!tick || type === "MT") return null;
    const { high: dh, low: dl, upper_circuit: uc, lower_circuit: lc } = tick;
    return {
      title: type === "RL" ? "Target Rules" : "Stop Loss Rules",
      upper: `Above: > ${fmtNum(dh)} & ≤ ${fmtNum(uc)}`,
      lower: `Below: < ${fmtNum(dl)} & ≥ ${fmtNum(lc)}`,
      reject: `Reject: between ${fmtNum(dl)} and ${fmtNum(dh)}`,
    };
  }, [tick, type]);

  if (!symbol) return null;

  const askPrice = tick ? fmtNum(tick.ask) : "";
  const bidPrice = tick ? fmtNum(tick.bid) : "";

  const priceCls =
    priceValidity === null ? "" :
    priceValidity === true ? "border-emerald-500 focus-visible:ring-emerald-400" :
                             "border-rose-500 focus-visible:ring-rose-400";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[380px] p-0 gap-0 rounded-xl overflow-hidden" data-testid="order-modal">
        <VisuallyHidden.Root>
          <DialogTitle>{`Place order on ${symbol}`}</DialogTitle>
        </VisuallyHidden.Root>
        {/* Header */}
        <div className="px-4 pt-4 pb-3">
          {onBehalfClientId && (
            <div className="mb-2 -mt-1 -mx-1 px-2 py-1 rounded bg-amber-50 border border-amber-300 text-amber-900 text-[10px] font-semibold inline-flex items-center gap-1.5"
                 data-testid="order-onbehalf-banner">
              <span className="uppercase tracking-wide">Trading for client</span>
              <span className="font-mono">{onBehalfClientId}</span>
            </div>
          )}
          <div className="flex items-start justify-between gap-2">
            <div className="text-[12px] leading-tight">
              <span className="font-extrabold text-[14px]">{symbol}</span>
              {meta && (
                <span className="text-muted-foreground">
                  {" "}[ <span className={meta.chgPts >= 0 ? "text-profit font-bold" : "text-loss font-bold"}>
                    {meta.chgPts >= 0 ? "+" : ""}{fmtNum(meta.chgPts)}
                  </span>, <span className={meta.chgPts >= 0 ? "text-profit font-bold" : "text-loss font-bold"}>
                    {meta.chgPct >= 0 ? "+" : ""}{fmtNum(meta.chgPct)}%
                  </span> ]
                  {" "}| <span className="font-semibold">Expiry</span> {meta.expiry}
                  {" "}| <span className="font-semibold">Lot</span> {meta.lot}
                </span>
              )}
            </div>
            <button onClick={() => onOpenChange(false)} data-testid="order-close"
                    className="h-6 w-6 grid place-items-center rounded-md hover:bg-secondary -mt-0.5 -mr-1">
              <X className="h-4 w-4" />
            </button>
          </div>
          {/* Live ref tiles */}
          {tick && type !== "MT" && (
            <div className="mt-2 grid grid-cols-4 gap-1.5 text-[10px]">
              <RefTile label="Day H" value={fmtNum(tick.high)} />
              <RefTile label="Day L" value={fmtNum(tick.low)} />
              <RefTile label="UC" value={fmtNum(tick.upper_circuit)} />
              <RefTile label="LC" value={fmtNum(tick.lower_circuit)} />
            </div>
          )}
        </div>

        <hr className="border-border" />

        {/* Form Row: Type / Qty / Price */}
        <div className="px-4 py-4 grid grid-cols-3 gap-3">
          <div>
            <Label htmlFor="otype" className="text-xs font-bold">Type</Label>
            <select id="otype" value={type} onChange={(e) => setType(e.target.value)}
                    data-testid="order-type"
                    className="mt-1 h-9 w-full rounded-md border border-border bg-background px-2 text-sm">
              <option value="MT">MT</option>
              <option value="RL">RL</option>
              <option value="SL">SL</option>
            </select>
            <div className="text-[9px] text-muted-foreground mt-0.5 leading-tight">
              {type === "MT" && "Market"}
              {type === "RL" && "Target"}
              {type === "SL" && "Stop Loss"}
            </div>
          </div>
          <div>
            <Label htmlFor="oqty" className="text-xs font-bold">Qty</Label>
            <Input id="oqty" type="number" min={1} value={qty}
                   onChange={(e) => setQty(e.target.value)}
                   data-testid="order-qty"
                   className="mt-1 h-9" />
          </div>
          <div>
            <Label htmlFor="oprice" className="text-xs font-bold">
              Price {type === "RL" && <span className="text-[9px] text-muted-foreground font-normal">(target)</span>}
              {type === "SL" && <span className="text-[9px] text-muted-foreground font-normal">(SL)</span>}
            </Label>
            <Input id="oprice" type="number" step={meta?.tick || 0.05}
                   value={type === "MT" ? "0" : price}
                   onChange={(e) => setPrice(e.target.value)}
                   disabled={type === "MT"}
                   data-testid="order-price"
                   className={`mt-1 h-9 disabled:bg-secondary/60 ${priceCls}`} />
          </div>
        </div>

        {/* Rule hint (only RL/SL) */}
        {ruleHint && (
          <div className="px-4 pb-3 -mt-2">
            <div className="text-[10px] rounded-md bg-secondary/50 border border-border px-2.5 py-1.5 leading-tight">
              <div className="font-bold uppercase tracking-wider text-[9px] text-muted-foreground mb-0.5">
                {ruleHint.title}
              </div>
              <div className="font-semibold text-profit">{ruleHint.upper}</div>
              <div className="font-semibold text-profit">{ruleHint.lower}</div>
              <div className="font-semibold text-loss mt-0.5">{ruleHint.reject}</div>
            </div>
          </div>
        )}

        <hr className="border-border" />

        {/* Action buttons */}
        <div className="px-4 py-4 flex items-center justify-center gap-3">
          <button onClick={() => submit("BUY")} disabled={busy}
                  data-testid="order-buy"
                  className="px-5 h-10 rounded-md border-2 font-bold text-[14px] transition-colors disabled:opacity-50
                             hover:bg-blue-50"
                  style={{ borderColor: "hsl(var(--buy))", color: "hsl(var(--buy))" }}>
            Buy{type === "MT" && tick ? ` (${askPrice})` : ""}
          </button>
          <button onClick={() => submit("SELL")} disabled={busy}
                  data-testid="order-sell"
                  className="px-5 h-10 rounded-md border-2 font-bold text-[14px] transition-colors disabled:opacity-50
                             hover:bg-rose-50"
                  style={{ borderColor: "hsl(var(--sell))", color: "hsl(var(--sell))" }}>
            Sell{type === "MT" && tick ? ` (${bidPrice})` : ""}
          </button>
        </div>

        {onBehalfClientId && (
          <div className="px-4 pb-4 -mt-2">
            <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2 py-1.5">
              Placing on behalf of <span className="font-bold font-mono">{onBehalfClientId}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RefTile({ label, value }) {
  return (
    <div className="rounded-md bg-secondary/60 px-1.5 py-1 text-center">
      <div className="text-[8px] uppercase tracking-wider text-muted-foreground font-bold leading-tight">{label}</div>
      <div className="num text-[10px] font-bold leading-tight">{value}</div>
    </div>
  );
}
